/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { BEM, T, useTranslate } from "@adapptio/lib-components";
import { Icon, ICON_SIZE } from "../../../../ui/components/Icon";
import { IIntegrationType } from "../../../../integration/IntegrationServiceTypes";

export interface IIntegrationEditorDetailsProps {
	className?: string;
	integrationType?: IIntegrationType;
}

export const IntegrationEditorDetails: React.FC<IIntegrationEditorDetailsProps> = ({
	className,
	integrationType
}) => {
	const baseClass = BEM("edt-integration-editor-details");
	const t = useTranslate();

	if (className) {
		baseClass.add(className);
	}

	const sortedFunctions = useMemo(() => {
		if (!integrationType?.functions) {
			return [];
		}
		return [...integrationType.functions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}, [integrationType?.functions]);

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("info").toClassName()}>
				<div className={baseClass.element("info-icon").toClassName()}>
					<Icon name={integrationType?.icon} size={ICON_SIZE.LARGE} />
				</div>
				<div className={baseClass.element("info-text").toClassName()}>
					<div className={baseClass.element("info-text-primary").toClassName()}>
						{integrationType?.label}
					</div>
					<div className={baseClass.element("info-text-secondary").toClassName()}>
						{integrationType?.description}
					</div>
				</div>
			</div>

			{sortedFunctions.length > 0 && (
				<div className={baseClass.element("functions").toClassName()}>
					<div className={baseClass.element("functions-title").toClassName()}>
						{t("editor", "Provided Functions")}
					</div>
					<ul className={baseClass.element("functions-list").toClassName()}>
						{sortedFunctions.map((fn) => (
							<li
								key={fn.functionName}
								className={baseClass.element("function-item").toClassName()}
							>
								<span className={baseClass.element("function-icon").toClassName()}>
									<Icon name={fn.icon} size={ICON_SIZE.SMALL} />
								</span>
								<span className={baseClass.element("function-info").toClassName()}>
									<span className={baseClass.element("function-label").toClassName()}>
										<T domain="editor" term={fn.label} />
									</span>
									<span className={baseClass.element("function-description").toClassName()}>
										<T domain="editor" term={fn.description} />
									</span>
								</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};
